<template>
  <div class="score-distribution-content">
    <div class="score-distribution-header">
      <h2>Score Distribution</h2>
      <div class="toggle-mode" :class="{ 'dark-mode': !hasMadeGuessInInfiniteMode }">
        <span class="infinite-symbol">♾️</span> Mode:
        {{ hasMadeGuessInInfiniteMode ? 'On' : 'Off' }}
      </div>
    </div>

    <div class="chart-container-holder">
      <div class="chart-container">
        <div class="chart-inner">
          <canvas ref="scoreChart"></canvas>
        </div>
        <div class="better-indicator"><span class="better-arrow">←</span> Better</div>
      </div>
    </div>

    <div class="score-info">
      <div class="achievement-score">
        Score: <span>{{ score }}</span> | Rank: <span>{{ formatNumber(rank) }}</span> of
        {{ formatNumber(plays) }}
      </div>
    </div>

    <div class="stats-grid">
      <div class="stats-box">
        <div class="stats-label">Total Legendary</div>
        <div class="stats-value gold">{{ numLegendary }}</div>
      </div>
      <div class="stats-box">
        <div class="stats-label">Percentile</div>
        <div class="stats-value gold">{{ percentile }}%</div>
      </div>
      <div class="stats-box">
        <div class="stats-label">Missed Squares</div>
        <div class="stats-value gold">{{ missedSquares }}</div>
      </div>
    </div>
  </div>
</template>

<script>
  import Chart from 'chart.js/auto';

  export default {
    name: 'ScoreDistributionTab',
    props: [
      'boardId',
      'board',
      'score',
      'rank',
      'plays',
      'statsData',
      'coordsAndRarities',
      'rarityPercentages',
      'hasMadeGuessInInfiniteMode',
    ],

    mounted() {
      this.$nextTick(() => {
        this.initScoreChart();
      });
    },

    computed: {
      percentile() {
        const percent = 1 - +this.rank / +this.plays;
        return Math.round(1000 * percent) / 10;
      },
      missedSquares() {
        let numAnswers = 0;
        for (let i = 0; i < this.board.length; i++) {
          for (let j = 0; j < this.board[i].length; j++) {
            if (this.board[i][j] && this.board[i][j] != '') {
              numAnswers++;
            }
          }
        }
        return 9 - numAnswers;
      },
      numLegendary() {
        const legendarySquares = this.coordsAndRarities.filter(([, , r]) => r < 5);
        return legendarySquares.length;
      },
    },

    methods: {
      formatNumber(num) {
        if (!num) return '';
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      },
      initScoreChart() {
        const ctx = this.$refs.scoreChart.getContext('2d');

        // Sample data for the bell curve
        const data = [];
        const rightBackgroundData = [];
        const leftBackgroundData = [];

        const buckets = this.hasMadeGuessInInfiniteMode
          ? this.statsData.scores_by_bucket.total_inf
          : this.statsData.scores_by_bucket.total;
        for (const key in buckets) {
          if (key == '_divisor') continue;
          const x = parseInt(key) * buckets._divisor;
          const y = buckets[key];
          data.push({ x, y });
          rightBackgroundData.push({ x, y: x < +this.score ? 0 : y });
          leftBackgroundData.push({ x, y: x > +this.score ? 0 : y });
        }

        // Destroy existing chart if it exists
        if (this.scoreChart) {
          this.scoreChart.destroy();
        }

        this.scoreChart = new Chart(ctx, {
          type: 'scatter',
          data: {
            datasets: [
              {
                label: 'Players',
                data,
                borderColor: 'rgba(173, 173, 235, 1)',
                borderWidth: 2,
                pointRadius: 0,
                tension: 0.4,
                showLine: true,
              },
              {
                data: rightBackgroundData,
                fill: true,
                backgroundColor: 'rgba(75, 192, 100, 0.2)',
                pointRadius: 0,
                tension: 0.4,
                showLine: true,
              },
              {
                data: leftBackgroundData,
                fill: true,
                backgroundColor: 'rgba(192, 75, 100, 0.2)',
                pointRadius: 0,
                tension: 0.4,
                showLine: true,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                enabled: true,
              },
              annotation: {
                annotations: {
                  line1: {
                    type: 'line',
                    xMin: this.score,
                    xMax: this.score,
                    borderColor: 'rgba(255, 255, 255, 0.8)',
                    borderWidth: 2,
                    label: {
                      display: true,
                      content: ['You', this.score],
                      position: 'end',
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      color: ['#1a1e2e', '#4f556e'],
                      font: {
                        weight: 'bold',
                      },
                    },
                  },
                },
              },
            },
            scales: {
              y: {
                title: {
                  display: true,
                  text: 'Players',
                  color: 'white',
                },
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)',
                },
                ticks: {
                  color: 'white',
                },
              },
              x: {
                min: 0,
                max: 900,
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)',
                },
                ticks: {
                  color: 'white',
                },
              },
            },
          },
        });
      },
    },
  };
</script>

<style scoped>
  .score-distribution-content {
    padding: 20px;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }

  .score-distribution-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }

  .score-distribution-header h2 {
    font-size: 24px;
    margin: 0;
  }

  .toggle-mode {
    background-color: rgba(147, 147, 236, 1);
    color: #1a1e2e;
    padding: 5px 15px;
    border-radius: 20px;
    font-weight: bold;
    display: flex;
    align-items: center;
  }

  .toggle-mode.dark-mode {
    background-color: #232736;
    color: #cccccc;
  }

  .infinite-symbol {
    margin-right: 5px;
  }

  .chart-container-holder {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }

  .chart-container {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    margin-bottom: 0;
    padding: 10px;
    padding-bottom: 0;
    justify-content: center;
  }

  .chart-inner {
    flex-grow: 1;
    height: 100px;
    max-height: 450px;
  }

  .better-indicator {
    color: #4caf50;
    font-weight: bold;
    text-align: left;
    padding-left: 55px;
    margin-bottom: 20px;
  }

  .better-arrow {
    font-size: 1.2em;
    margin-right: 5px;
  }

  .score-info {
    margin-bottom: 30px;
    text-align: center;
  }

  .achievement-score {
    color: #8d8f97;
  }

  .achievement-score span {
    color: rgba(147, 147, 236, 1);
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
  }

  .stats-box {
    background-color: #232736;
    border-radius: 8px;
    padding: 15px;
    text-align: center;
  }

  .stats-label {
    font-size: 14px;
    color: #8d8f97;
    margin-bottom: 5px;
  }

  .stats-value {
    font-size: 24px;
    font-weight: bold;
  }

  .gold {
    color: rgba(147, 147, 236, 1);
  }

  @media (max-width: 767px) {
    .stats-grid {
      background-color: #232736;
      border-radius: 8px;
      gap: 0;
    }

    .stats-box {
      padding: 0;
      margin: 5px 0;
      background: none;
      border-radius: 0;
      border-right: 1px solid #8d8f9766;
    }

    .stats-box:last-child {
      border-right: none;
    }

    .chart-container {
      height: 200px;
    }

    .stats-boxes {
      flex-direction: column;
      align-items: center;
      gap: 10px;
    }

    .stats-box {
      width: 100%;
      max-width: 200px;
    }

    .stats-value {
      font-size: 20px;
    }

    .score-info {
      font-size: 0.9rem;
    }
  }
</style>
